import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Filter,
    Download,
    Plus,
    Eye,
    Trash2,
    Calendar,
    AlertCircle,
    FileSpreadsheet,
    FileText,
    History,
    RotateCcw,
    Anchor,
    Box
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../components/ui/table";
import api from '../../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../../components/ui/utils';
import { toSydneyDate, formatTimestamp } from '../../../utils/helpers';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

interface LiftingRegisterListProps {
    onCreateNew: () => void;
}

const LiftingRegisterList: React.FC<LiftingRegisterListProps> = ({ onCreateNew }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [inspections, setInspections] = useState<any[]>([]);
    const [filters, setFilters] = useState({
        q: '',
        startDate: '',
        endDate: '',
        showRecycle: false
    });
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<number | null>(null);

    useEffect(() => {
        fetchInspections();
    }, [filters]);

    const fetchInspections = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (filters.q) params.append('q', filters.q);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            if (filters.showRecycle) params.append('recycle', 'true');

            const result = await api.get(`/api/assets/lifting-inspections?${params.toString()}`);
            setInspections(result);
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch lifting inspections');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (id: number) => {
        setItemToDelete(id);
        setIsDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        try {
            const endpoint = filters.showRecycle
                ? `/api/assets/lifting-inspections/${itemToDelete}/permanent`
                : `/api/assets/lifting-inspections/${itemToDelete}`;

            await api.delete(endpoint);
            toast.success(filters.showRecycle ? 'Inspection deleted permanently' : 'Inspection moved to recycle bin');
            fetchInspections();
        } catch (err) {
            console.error(err);
            toast.error('Failed to delete inspection');
        } finally {
            setIsDeleteDialogOpen(false);
            setItemToDelete(null);
        }
    };

    const handleRestore = async (id: number) => {
        try {
            await api.patch(`/api/assets/lifting-inspections/${id}/restore`, {});
            toast.success('Inspection restored successfully');
            fetchInspections();
        } catch (err) {
            console.error(err);
            toast.error('Failed to restore inspection');
        }
    };

    const handleExport = (type: 'csv' | 'pdf') => {
        if (type === 'csv') {
            exportToCSV();
        } else {
            exportToPDF();
        }
    };

    const exportToPDF = () => {
        if (inspections.length === 0) {
            toast.error('No data to export');
            return;
        }

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        doc.setFontSize(16);
        doc.text("Lifting Equipment Register", 14, 15);
        doc.setFontSize(8);
        doc.text(`Generated on: ${formatTimestamp(new Date().toISOString())}`, 14, 22);

        let y = 35;
        const headers = [
            'Asset Name', 'Asset ID', 'Description', 'Location', 'Serial No.',
            'Test Date', 'Next Due', 'Result', 'Action', 'Tested By', 'Licence', 'Comments'
        ];

        // Col widths for landscape A4 (approx 297mm total)
        const colWidths = [30, 20, 30, 25, 20, 20, 20, 15, 25, 20, 15, 37];

        // Draw headers
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        let x = 14;
        headers.forEach((h, i) => {
            doc.text(h, x, y);
            x += colWidths[i];
        });

        y += 4;
        doc.line(14, y - 1, 283, y - 1); // Draw line under headers
        y += 5;

        // Draw rows
        doc.setFont("helvetica", "normal");
        inspections.forEach((i, idx) => {
            if (y > 190) {
                doc.addPage();
                y = 20;
                doc.setFont("helvetica", "bold");
                let nx = 14;
                headers.forEach((h, hi) => {
                    doc.text(h, nx, y);
                    nx += colWidths[hi];
                });
                y += 4;
                doc.line(14, y - 1, 283, y - 1);
                y += 5;
                doc.setFont("helvetica", "normal");
            }
            let rowX = 14;
            const row = [
                (i.asset?.name || 'N/A').substring(0, 25),
                (i.asset?.tag || 'N/A').substring(0, 15),
                (i.asset?.description || '').substring(0, 25),
                (i.location || '').substring(0, 20),
                (i.asset?.serialNumber || 'N/A').substring(0, 15),
                toSydneyDate(i.testDate) || '',
                toSydneyDate(i.nextTestDue) || '',
                i.testResult || '',
                (i.nextAction || '').substring(0, 20),
                (i.testedBy || '').substring(0, 15),
                (i.licenceNo || '').substring(0, 10),
                (i.comments || '').substring(0, 30)
            ];
            row.forEach((cell, cellIdx) => {
                doc.text(String(cell || ''), rowX, y);
                rowX += colWidths[cellIdx];
            });
            y += 6;
        });

        doc.save(`Lifting_Register_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success('PDF exported successfully');
    };

    const exportToCSV = () => {
        if (inspections.length === 0) {
            toast.error('No data to export');
            return;
        }

        const headers = [
            'Asset Name', 'Asset ID', 'Equipment Description', 'Location', 'Serial No.',
            'Test Date', 'Next Test Due', 'Test Result', 'Next Action', 'Tested By', 'Licence No.', 'Comments'
        ];

        const rows = inspections.map(i => [
            `"${(i.asset?.name || '').replace(/"/g, '""')}"`,
            `"${(i.asset?.tag || '').replace(/"/g, '""')}"`,
            `"${(i.asset?.description || '').replace(/"/g, '""')}"`,
            `"${(i.location || '').replace(/"/g, '""')}"`,
            `"${(i.asset?.serialNumber || '').replace(/"/g, '""')}"`,
            toSydneyDate(i.testDate) || '',
            toSydneyDate(i.nextTestDue) || '',
            i.testResult || '',
            `"${(i.nextAction || '').replace(/"/g, '""')}"`,
            `"${(i.testedBy || '').replace(/"/g, '""')}"`,
            `"${(i.licenceNo || '').replace(/"/g, '""')}"`,
            `"${(i.comments || '').replace(/"/g, '""')}"`
        ]);

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Lifting_Register_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        toast.success('CSV exported successfully');
    };

    return (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="font-bold text-gray-900 text-lg">Lifting Equipment Register</h2>
                    <p className="text-gray-500 text-sm font-medium text-[13px]">Inspection records for lifting gear and slings</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setFilters({ ...filters, showRecycle: !filters.showRecycle })}
                        className={cn("rounded-xl gap-2 h-10 px-4 text-xs font-bold transition-all", filters.showRecycle && "bg-orange-50 text-orange-600 border-orange-100 hover:bg-orange-100")}
                    >
                        <History size={16} />
                        {filters.showRecycle ? "View Active" : "Recycle Bin"}
                    </Button>
                    <Button onClick={onCreateNew} className="bg-slate-950 hover:bg-slate-900 text-white rounded-xl gap-2 shadow-sm h-10 px-4 text-xs font-bold">
                        <Plus size={18} />
                        New Inspection
                    </Button>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pt-2">
                <div className="relative flex-1 w-full max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                        placeholder="Search records..."
                        className="pl-10 bg-gray-50/50 border-gray-100 focus:bg-white transition-all rounded-xl h-11 text-sm font-medium"
                        value={filters.q}
                        onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                    />
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Input
                            type="date"
                            className="h-11 rounded-xl border-gray-100 bg-gray-50/50 text-xs font-bold w-full sm:w-[140px]"
                            value={filters.startDate}
                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                        />
                        <span className="text-gray-300 text-xs font-bold px-1">to</span>
                        <Input
                            type="date"
                            className="h-11 rounded-xl border-gray-100 bg-gray-50/50 text-xs font-bold w-full sm:w-[140px]"
                            value={filters.endDate}
                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                        />
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                        <Button variant="outline" onClick={() => handleExport('pdf')} className="h-11 rounded-xl border-gray-100 text-gray-700 hover:bg-gray-50 gap-2 font-bold px-4 flex-1 sm:flex-none text-xs">
                            <FileText size={16} className="text-red-500" />
                            PDF
                        </Button>
                        <Button variant="outline" onClick={() => handleExport('csv')} className="h-11 rounded-xl border-gray-100 text-gray-700 hover:bg-gray-50 gap-2 font-bold px-4 flex-1 sm:flex-none text-xs">
                            <FileSpreadsheet size={16} className="text-green-600" />
                            CSV
                        </Button>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto no-scrollbar md:-mx-6 mt-4 md:mt-0">
                <table className="w-full border-collapse block md:table">
                    <thead className="hidden md:table-header-group">
                        <tr className="bg-gray-50/50">
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest first:rounded-tl-2xl">Asset / ID</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden md:table-cell">Location</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden lg:table-cell">Test Date</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest hidden lg:table-cell">Next Due</th>
                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Result</th>
                            <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest last:rounded-tr-2xl">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="block md:table-row-group space-y-4 md:space-y-0 md:divide-y divide-gray-100">
                        {loading ? (
                            [...Array(3)].map((_, i) => (
                                <tr key={i} className="animate-pulse block md:table-row bg-white md:bg-transparent border border-gray-100 md:border-0 rounded-2xl md:rounded-none mb-4 md:mb-0">
                                    <td colSpan={6} className="px-6 py-8 block md:table-cell"><div className="h-10 bg-gray-50 rounded-xl w-full" /></td>
                                </tr>
                            ))
                        ) : inspections.length === 0 ? (
                            <tr className="block md:table-row">
                                <td colSpan={6} className="px-6 py-20 text-center text-gray-400 block md:table-cell">
                                    <div className="flex flex-col items-center">
                                        <Anchor size={48} className="mb-4 opacity-20" />
                                        <p className="font-bold text-gray-500">No inspections found</p>
                                        <p className="text-xs uppercase tracking-widest mt-1">Try adjusting your filters or search query</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            inspections.map((item) => (
                                <tr key={item.id} className="block md:table-row hover:bg-gray-50/50 transition-colors group bg-white md:bg-transparent border border-gray-100 md:border-0 rounded-2xl md:rounded-none mb-4 md:mb-0 relative py-4 md:py-0 shadow-sm md:shadow-none">
                                    <td className="px-4 md:px-6 py-2 md:py-5 block md:table-cell border-b border-gray-100 md:border-0 pb-4 md:pb-5">
                                        <div className="flex items-start md:items-center gap-4">
                                            <div className={cn(
                                                "w-16 h-16 md:w-10 md:h-10 rounded-xl flex items-center justify-center shrink-0 border mt-1 md:mt-0",
                                                item.testResult === 'Pass' ? "bg-amber-50 text-amber-600 border-amber-100" : "bg-red-50 text-red-600 border-red-100"
                                            )}>
                                                <Anchor size={18} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex justify-between items-start md:block">
                                                    <p className="font-bold text-gray-900 truncate text-base md:text-sm">{item.asset?.name || 'Deleted Asset'}</p>
                                                    <Badge variant="outline" className={cn(
                                                        "rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] md:hidden shrink-0 ml-2",
                                                        item.testResult === 'Pass' ? "bg-amber-50 text-amber-600 border-amber-100" : "bg-red-50 text-red-600 border-red-100"
                                                    )}>
                                                        {item.testResult}
                                                    </Badge>
                                                </div>
                                                <p className="text-[11px] md:text-[10px] font-black text-gray-400 tracking-wider uppercase mt-1 md:mt-0">
                                                    TAG: {item.asset?.tag || 'N/A'}
                                                </p>
                                                <div className="md:hidden mt-2 flex items-center gap-2 text-xs font-bold text-gray-600">
                                                    <span className="text-gray-400 uppercase text-[9px]">Location:</span> {item.location}
                                                </div>
                                            </div>
                                        </div>
                                    </td>

                                    <td className="px-6 py-5 hidden md:table-cell">
                                        <div className="flex items-center gap-2 text-gray-600 font-bold text-sm">
                                            {item.location}
                                        </div>
                                    </td>

                                    <td className="px-6 py-5 hidden lg:table-cell">
                                        <div className="flex items-center gap-2 text-sm font-bold text-gray-400">
                                            <Calendar size={14} />
                                            {toSydneyDate(item.testDate)}
                                        </div>
                                    </td>

                                    <td className="px-6 py-5 hidden lg:table-cell">
                                        <div className="flex items-center gap-2 text-sm font-black text-gray-900">
                                            {toSydneyDate(item.nextTestDue)}
                                        </div>
                                    </td>

                                    <td className="px-6 py-5 hidden md:table-cell">
                                        <Badge variant="outline" className={cn(
                                            "rounded-lg border-0 font-bold px-3 py-1 text-xs",
                                            item.testResult === 'Pass' ? "bg-amber-50 text-amber-600 border-amber-100" : "bg-red-50 text-red-600 border-red-100"
                                        )}>
                                            {item.testResult}
                                        </Badge>
                                    </td>

                                    <td className="px-4 md:px-6 py-4 md:py-5 block md:table-cell border-t border-gray-50 md:border-t-0 mt-3 md:mt-0">
                                        <div className="flex items-center justify-end gap-2 w-full">
                                            {filters.showRecycle ? (
                                                <Button
                                                    variant="outline"
                                                    className="h-10 md:h-9 px-4 md:px-0 md:w-9 border-gray-200 hover:bg-green-50 rounded-xl hover:text-green-600 hover:border-green-100 transition-all flex-1 md:flex-none justify-center"
                                                    onClick={() => handleRestore(item.id)}
                                                >
                                                    <RotateCcw size={18} className="md:mr-0 mr-2 shrink-0" />
                                                    <span className="md:hidden font-bold">Restore</span>
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outline"
                                                    className="h-10 md:h-9 px-4 md:px-0 md:w-9 border-gray-200 hover:bg-blue-50 rounded-xl hover:text-blue-600 hover:border-blue-100 transition-all flex-1 md:flex-none justify-center"
                                                    onClick={() => navigate(`/assets/lifting-inspections/${item.id}`)}
                                                >
                                                    <Eye size={18} className="md:mr-0 mr-2 shrink-0" />
                                                    <span className="md:hidden font-bold">View</span>
                                                </Button>
                                            )}
                                            <Button
                                                variant="outline"
                                                className="h-10 md:h-9 px-4 md:px-0 md:w-9 border-gray-200 hover:bg-red-50 rounded-xl hover:text-red-600 hover:border-red-100 transition-all flex-1 md:flex-none justify-center"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                <Trash2 size={18} className="md:mr-0 mr-2 shrink-0" />
                                                <span className="md:hidden font-bold">
                                                    {filters.showRecycle ? "Delete" : "Remove"}
                                                </span>
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent className="rounded-3xl border-gray-100">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold">
                            {filters.showRecycle ? "Permanently delete record?" : "Are you sure?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-500 font-medium">
                            {filters.showRecycle
                                ? "This action cannot be undone. This record will be permanently removed from the system."
                                : "This will move the lifting inspection record to the recycle bin. It will be kept for 180 days before permanent deletion."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2 sm:gap-0">
                        <AlertDialogCancel className="rounded-xl font-bold mr-2">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold"
                        >
                            {filters.showRecycle ? "Yes, delete permanently" : "Yes, move to recycle bin"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default LiftingRegisterList;
